/* eslint-disable prettier/prettier */
import { reactive, computed, ref, ComputedRef } from 'vue'
import { Collection, CollectionParams } from '@/types'
import { AdviceStatus } from './useAdvicesStatuses'
import { InvoiceStatus } from './useInvoiceStatuses'
import { useApi } from './useApi'
import { useAdviceDocuments } from './useAdviceDocuments'

const { invoiceData, removeAdviceFromStorage } = useAdviceDocuments()

export type Product = {
    [key: string]: string
    name: string
    shortName: string
}

export type Address = {
    [key: string]: string | undefined
    city: string
    address: string
    postcode: string
    fullAddress: string
    note?: string | undefined
}

export type PurchaseContract = {
    number: string
    supplier: {
        name: string
    }
}

export type SalesContract = {
    number: string
    recipient: {
        name: string
    }
}

export type Currency = {
    [key: string]: string
    symbol: string
}

export type IdentyficationType = {
    [key: string]: string
    name: string
}

export type AdviceSchedule = {
    [key: string]: string | number | null
    date: string
    dateTo: string
    quantity: number
}

export type AdviceEmail = {
    id: number
    adviceId: number
    adviceEmailId: number
    adviceEmail: {
        id: number
        email: string
        name: string
        description: string
        createdAt: string
        updatedAt: string | null
        phone: string | null
        fullName: string
    }
}

export type AdviceCarrierInvoice = {
    amount: number
    carrierId: number
    createdAt: string
    description: string
    filename: string | null
    filenamePdf: string
    id: number
    imageId: number
    importStatusId: null
    inputdate: string | null
    inputloginid: number | null
    inputloginname: string | null
    invoiceStatus: InvoiceStatus
    invoiceStatusId: number
    number: string | null
    transportRate: number | null
    updatedAt: string | null
}

export type Advice = {
    [key: string]:
        | number
        | string
        | Product
        | Address
        | PurchaseContract
        | SalesContract
        | AdviceStatus
        | AdviceCarrierInvoice[]
        | Currency
    adviceStatus: AdviceStatus
    amount: string
    amountCurrency: Currency
    axId: string
    carrierInvoices: AdviceCarrierInvoice[]
    deliveryDate: string
    driverFirstName: string
    driverLastName: string
    driverPhone: string
    id: number
    loadingDate: string
    loadingTimeFrom: string
    loadingTimeTo: string
    note: string
    product: Product
    purchaseContract: PurchaseContract
    quantity: string
    recipientAdviceAddress: Address
    salesContract: SalesContract
    supplierAdviceAddress: Address
    timeFrom: string
    timeTo: string
    transportRate: string
    vehicleIdentificationNumber: string
}

export type AdviceDetails = Advice & {
    adviceRecipientsEmails: AdviceEmail[]
    adviceSuppliersEmails: AdviceEmail[]
    amountCurrency: Currency
    currency: Currency
    identyficationNumber: string
    identyficationType: IdentyficationType
    pin: string
    product: Product
    recipientAdviceEmailsWithName: string
    schedule: AdviceSchedule
    supplierAdviceEmailsWithName: string
}

export type AdviceListFilters = {
    adviceStatusId: string
    deliveryDate: string
    driverFirstName: string
    driverLastName: string
    invoiceStatusId: string
    loadingDate: string
    vehicleIdentificationNumber: string
}

const advices = reactive<Collection<Advice>>({} as Collection<Advice>)
const loading = ref<boolean>(false)
const isEmpty = computed(() => advices.items?.length === 0)

export const useAdvices = (): {
    fetchAdvices: (params?: CollectionParams<AdviceListFilters>) => Promise<void>
    fetchAdviceDetails: (adviceId: number) => Promise<void>
    sendSmsToDriver: (adviceId: number) => Promise<void>
    advices: ComputedRef<Collection<Advice>>
    adviceDetails: ComputedRef<AdviceDetails>
    loading: ComputedRef<boolean>
    isEmpty: ComputedRef<boolean>
} => {
    const { useGet } = useApi()
    const fetchAdvices = async (params?: CollectionParams<AdviceListFilters>) => {
        loading.value = true
        try {
            const _params_ = params
                ? {
                    page: params.page,
                    ...(params?.filters && { ...params?.filters }),
                }
                : { page: 1 }
            advices.items = []
            const { status, data } = await useGet('/advices', { params: _params_ })
            if (status === 200 && data.data.items) {
                advices.items = data.data.items
                advices.metadata = data.data.metadata

                // remove advice from invoice data when advice has invoice added
                invoiceData.value.advices.forEach(invoiceAdvice => {
                    const foundAdvice = advices.items.find(advice => advice.id === invoiceAdvice.id)
                    if(foundAdvice && foundAdvice.carrierInvoices && foundAdvice.carrierInvoices[0]) {
                        removeAdviceFromStorage(invoiceAdvice.id)
                    }
                })
            }
        } finally {
            loading.value = false
        }
    }

    const adviceDetails = ref<AdviceDetails>({} as AdviceDetails)
    const fetchAdviceDetails = async (adviceId: number) => {
        loading.value = true
        try {
            const { status, data } = await useGet(`/advice/${adviceId}`)
            if (status === 200 && data.data) {
                adviceDetails.value = data.data
            }
        } finally {
            loading.value = false
        }
    }

    const sendSmsToDriver = async (adviceId: number) => {
        await useGet(`/advices/send_driver_sms/${adviceId}`)
    }

    return {
        fetchAdvices,
        fetchAdviceDetails,
        sendSmsToDriver,
        advices: computed(() => advices),
        adviceDetails: computed(() => adviceDetails.value),
        loading: computed(() => loading.value),
        isEmpty,
    }
}
